import { MockupState, ModalType, mockupState } from '../../../Recoil/RecoilState';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import Button, { ButtonColor } from '../buttons/Button';
import { ReactComponent as DownloadSimple } from '../../../assets/icons/download-simple.svg';
import { ReactComponent as CrownSimple } from '../../../assets/icons/crown-simple.svg';
import SpinnerVexels from '../spinner/SpinnerVexels';
import UserModal from './UserModal';
import isMobile from 'ismobilejs';
import logo from '../../../assets/logo.png';
import produce from 'immer';
import { removeToken } from '../../../utils/AuthCookies';
import { screenSize } from '../../../stylesGlobal/Variables';
import { useRecoilState } from 'recoil';

interface Props {
  isMobile: boolean;
  isEditing: boolean;
  isLoadingUser?: boolean;
}

const HeaderWrapper = styled.header<Props>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--zIndex-high);
  width: 100%;
  height: 5.4rem;
  background-color: var(--color-white);
  box-shadow: 0 3px 21px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  ${(props) =>
    props.isMobile
      ? css`
          height: 59px;
        `
      : null}

  .overlay {
    position: absolute;
    width: 100%;
    height: 16vh;
    left: 0;
    top: 0;
    background: linear-gradient(180deg, #000000b8, transparent);
    display: ${(props) => (props.isEditing ? 'block' : 'none')};
  }

  .header__leftCol {
    min-width: 280px;
    display: flex;
    align-items: center;
    gap: 1rem;

    img {
      padding-left: 1.2em;
      ${(props) =>
        props.isMobile
          ? css`
              max-width: 100%;
              padding: 1rem 0 1rem 2rem;
            `
          : null}
    }

    h1 {
      font-size: 1.2rem;
      font-weight: 300;
      color: #9d9d9d;
      padding-right: 0.7rem;
      ${(props) =>
        props.isMobile
          ? css`
              display: none;
            `
          : null}
    }

    span {
      font-size: 1.2rem;
      font-weight: 500;
      color: var(--color-gray-800);
    }
  }
  .header__rightCol {
    height: 100%;
    display: flex;
    align-items: center;
    gap: 2.1em;
    padding-right: 2rem;
    ${(props) =>
      props.isMobile
        ? css`
            width: auto;
            text-align: right;
          `
        : null}

    .login-container {
      display: flex;
      gap: 2.1em;

      .login-item {
        text-decoration: none;
        font-size: 1.4rem;
        font-weight: 500;
        color: var(--color-gray-800);
        cursor: pointer;
      }
    }

    .header_userAvatar {
      display: block;
      width: 4rem;
      height: 4rem;
      overflow: hidden;
      border-radius: 100%;
      display: flex;
      justify-content: flex-end;
      cursor: pointer;
      background-color: var(--color-light-gray);
      border: 1px solid transparent;

      &:focus {
        outline: none;
        border: 1px solid var(--color-gray-800);
      }
      img {
        object-fit: cover;
      }
    }
  }

  .w-20 {
    width: 20px;
  }
  .h-20 {
    height: 20px;
  }

  @media (min-width: ${screenSize.size_m}) {
    .header__leftCol {
      img {
        max-height: 30px;
      }
    }
  }
`;

const Header: React.FC<Props> = (props) => {
  const [mockupGeneralState, setMockupGeneralState] = useRecoilState(mockupState);
  const [openUserModal, setOpenUserModal] = useState(false);

  const handleDownload = () => {
    setMockupGeneralState(
      produce((draftState: MockupState) => {
        const isPremium = draftState.mockup.premium && !draftState.user.hasMockupsAccess;
        draftState.modal = {
          open: true,
          type: draftState.user.loggedIn
            ? isPremium
              ? ModalType.mockupsAccess
              : draftState.user.hasRemainingDownloads
                ? ModalType.download
                : ModalType.noMoreDownloads
            : ModalType.login,
        };
      }),
    );
  };

  const handleLogout = () => {
    removeToken();
    setOpenUserModal(false);
    setMockupGeneralState(
      produce((draftState: MockupState) => {
        draftState.user = {
          loggedIn: false,
          token: undefined,
          name: null,
          avatar: null,
          planType: null,
          watermark: true,
          isSubscriptor: false,
          hasMockupsAccess: false,
          hasRemainingDownloads: false,
        };
      }),
    );
    sessionStorage.removeItem('user');
  };

  const toggleUserModal = () => {
    setOpenUserModal((prevState) => !prevState);
  };
  const handleLogin = () => {
    setMockupGeneralState(
      produce((draftState: MockupState) => {
        draftState.modal = {
          open: true,
          type: ModalType.login,
        };
      }),
    );
  };
  const handleSignUp = () => {
    setMockupGeneralState(
      produce((draftState: MockupState) => {
        draftState.modal = {
          open: true,
          type: ModalType.signUp,
        };
      }),
    );
  };
  const renderUserInfo = (isLoadingUser?: boolean) => {
    if (isLoadingUser) {
      return <SpinnerVexels scale={0.2} />;
    } else if (mockupGeneralState.user.loggedIn) {
      return (
        <button className="header_userAvatar" onClick={toggleUserModal}>
          {mockupGeneralState.user.avatar && (
            <img
              src={mockupGeneralState.user.avatar}
              alt={`${mockupGeneralState.user.name} Avatar`}
            />
          )}
        </button>
      );
    } else {
      return (
        <div className="login-container">
          <span className="login-item" onClick={handleSignUp}>
            Sign up
          </span>
          <span onClick={handleLogin} className="login-item">
            Log in
          </span>
        </div>
      );
    }
  };

  return (
    <>
      <HeaderWrapper isMobile={isMobile(window.navigator).any} isEditing={props.isEditing}>
        {props.isMobile && <div className="overlay" />}
        <div className="header__leftCol">
          <a href={'https://www.vexels.com/'} target={'blank'}>
            <img src={logo} alt="Vexels logo" />
          </a>
          {!props.isMobile && (
            <Button
              color={ButtonColor.gray}
              label="Upgrade"
              large={true}
              padding="10px 20px"
              borderRadius="var(--rounded-full)"
              gap="6px"
              onClick={() => {
                window.open('https://www.vexels.com/merch-plans/mg/', '_blank');
              }}
              iconSVG={<CrownSimple className="w-20 h-20" />}
            />
          )}
          {mockupGeneralState.user.watermark && <span>Upgrade to remove watermarks</span>}
        </div>
        <div className="header__rightCol">
          {!props.isMobile && (
            <Button
              color={ButtonColor.gray}
              iconSVG={<DownloadSimple className="w-20 h-20" />}
              label="Download"
              onClick={handleDownload}
              large={true}
              disabled={!mockupGeneralState.mockup.mockupReady}
              padding="10px 20px"
              borderRadius="var(--rounded-full)"
              gap="6px"
            />
          )}
          {renderUserInfo(props.isLoadingUser)}
        </div>
      </HeaderWrapper>
      {openUserModal && (
        <UserModal
          isOpen={openUserModal}
          onClose={toggleUserModal}
          onLogout={handleLogout}
          user={mockupGeneralState.user}
        />
      )}
    </>
  );
};

export default Header;
